
import React from 'react';
import { ShieldCheck, Mail, ArrowLeft, Heart, AlertCircle } from './Icons';

interface LegalPageProps {
  type: 'privacy' | 'coppa' | 'support';
  onBack: () => void;
}

const LegalPages: React.FC<LegalPageProps> = ({ type, onBack }) => {
  const content = {
    privacy: {
      title: "Privacy Policy",
      icon: <ShieldCheck className="w-12 h-12 text-gold-400" />,
      text: (
        <div className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-white">1. Data Collection</h3>
            <p>At StoryForge Kids, we prioritize your family's privacy. We collect minimal data required to create stories: your name, email, and the child's profile details (name, age, and appearance).</p>
          </section>
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-white">2. AI Image Processing</h3>
            <p>Photos uploaded for child profiles are processed securely via encrypted channels and stored locally in your browser (IndexedDB). They are never used to train external AI models.</p>
          </section>
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-white">3. Third Parties</h3>
            <p>We use Google Gemini for story and image generation and Lemon Squeezy for secure payment processing. We do not sell your data to advertisers.</p>
          </section>
        </div>
      )
    },
    coppa: {
      title: "COPPA Compliance",
      icon: <AlertCircle className="w-12 h-12 text-forge-400" />,
      text: (
        <div className="space-y-6">
          <p className="italic text-forge-200">The Children's Online Privacy Protection Act (COPPA) is a US law designed to protect the privacy of children under 13.</p>
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-white">Parental Control</h3>
            <p>StoryForge Kids is a "Parent-Directed" application. Accounts must be created by an adult (parent or guardian). We do not allow children to create accounts independently.</p>
          </section>
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-white">Our Commitment</h3>
            <p>We do not collect more information from a child than is reasonably necessary to provide the personalized storybook service. Parents can delete their child's profile and story library at any time.</p>
          </section>
        </div>
      )
    },
    support: {
      title: "Customer Support",
      icon: <Mail className="w-12 h-12 text-spark-400" />,
      text: (
        <div className="space-y-8 text-center">
          <p className="text-lg">Need help with your magical adventures? Our team is here to assist you.</p>
          <div className="bg-white/5 p-8 rounded-3xl border border-white/10 space-y-4">
             <h4 className="text-gold-400 font-bold uppercase tracking-widest text-sm">Direct Contact</h4>
             <a href="mailto:support@storyforgekidsai.com" className="text-2xl md:text-3xl font-bold text-white hover:text-gold-400 transition-colors block underline">
                support@storyforgekidsai.com
             </a>
             <p className="text-gray-500 text-sm italic">We typically respond within 24 hours.</p>
          </div>
          <div className="flex justify-center gap-8">
             <div className="flex flex-col items-center gap-2">
                <Heart className="w-6 h-6 text-pink-500" />
                <span className="text-xs font-bold uppercase tracking-tighter">Family First</span>
             </div>
             <div className="flex flex-col items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-green-500" />
                <span className="text-xs font-bold uppercase tracking-tighter">Safe & Secure</span>
             </div>
          </div>
        </div>
      )
    }
  };

  const page = content[type];

  return (
    <div className="min-h-screen bg-[#0f0e17] pt-10 px-4 pb-20">
      <div className="max-w-3xl mx-auto space-y-12 animate-slide-up">
        <button 
            onClick={onBack}
            className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors group"
        >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-bold uppercase tracking-widest text-xs">Back to Home</span>
        </button>

        <div className="flex flex-col items-center text-center space-y-6">
            <div className="p-6 bg-white/5 rounded-full border border-white/10 shadow-xl">
                {page.icon}
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white">{page.title}</h1>
        </div>

        <div className="glass-panel p-8 md:p-12 rounded-[3rem] border-white/10 text-gray-300 leading-relaxed font-light text-lg">
            {page.text}
        </div>
      </div>
    </div>
  );
};

export default LegalPages;
